import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function formatNumber(n) {
  return Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function StockManager({ authConfig, backendUrl, onUnauthorized, stores = [], selectedStoreId = '', onStoreChange }) {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [qtyFilter, setQtyFilter] = useState(''); // '', 'negative', 'nonzero', 'zero'

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = selectedStoreId ? { storeId: selectedStoreId } : {};
      const { data } = await axios.get(`${backendUrl}/api/reports/inventory`, { params, ...authConfig });
      setInventory(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to load stock.');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, authConfig, selectedStoreId, onUnauthorized]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  const products = useMemo(() => inventory?.products || [], [inventory?.products]);
  const categories = useMemo(() => {
    const cats = new Set(products.map((p) => p.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.barcode || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      );
    }
    if (selectedGroup) list = list.filter((p) => (p.category || '') === selectedGroup);
    if (qtyFilter === 'negative') list = list.filter((p) => (p.quantityOnHand ?? 0) < 0);
    else if (qtyFilter === 'nonzero') list = list.filter((p) => (p.quantityOnHand ?? 0) !== 0);
    else if (qtyFilter === 'zero') list = list.filter((p) => (p.quantityOnHand ?? 0) === 0);
    return list;
  }, [products, search, selectedGroup, qtyFilter]);

  const summary = useMemo(() => {
    let negative = 0, zero = 0, positive = 0;
    for (const p of filteredProducts) {
      const q = p.quantityOnHand ?? 0;
      if (q < 0) negative++;
      else if (q === 0) zero++;
      else positive++;
    }
    return { negative, zero, positive };
  }, [filteredProducts]);

  const totalCost = filteredProducts.reduce((s, p) => s + (p.value ?? 0), 0);
  const totalSaleValue = filteredProducts.reduce((s, p) => s + ((p.quantityOnHand ?? 0) * (p.price ?? p.cost ?? 0)), 0);

  const handleRefresh = () => loadInventory();
  const handlePrint = () => window.print();
  const handleExcel = () => {
    const cols = ['Code', 'Name', 'Quantity', 'Unit', 'Cost price', 'Cost', 'Value'];
    const rows = filteredProducts.map((p) => [
      p.sku || p._id?.slice(-6),
      p.name,
      p.quantityOnHand ?? 0,
      'pc',
      p.cost ?? 0,
      p.value ?? 0,
      ((p.quantityOnHand ?? 0) * (p.price ?? p.cost ?? 0))
    ]);
    const header = cols.join('\t');
    const body = rows.map((r) => r.join('\t')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
        <h2 className="text-lg font-semibold">Management • Stock</h2>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleRefresh} className="px-2 py-1 rounded text-sm border hover:bg-gray-100">Refresh</button>
          <button type="button" className="px-2 py-1 rounded text-sm border">Stock history</button>
          <button type="button" onClick={handlePrint} className="px-2 py-1 rounded text-sm border">Print</button>
          <button type="button" onClick={handlePrint} className="px-2 py-1 rounded text-sm border">Save as PDF</button>
          <button type="button" onClick={handleExcel} className="px-2 py-1 rounded text-sm border">Excel</button>
          <button type="button" className="px-2 py-1 rounded text-sm border">Inventory count report</button>
          <button type="button" className="px-2 py-1 rounded text-sm border">Quick inventory</button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left: product groups */}
        <div className="w-56 border-r overflow-y-auto p-2 shrink-0">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Product groups</p>
          <button type="button" onClick={() => setSelectedGroup('')} className={`w-full text-left px-2 py-1.5 rounded text-sm mb-1 ${!selectedGroup ? 'bg-indigo-100 text-indigo-800' : 'hover:bg-gray-100'}`}>All</button>
          {categories.map((c) => (
            <button key={c} type="button" onClick={() => setSelectedGroup(c)} className={`w-full text-left px-2 py-1.5 rounded text-sm mb-1 ${selectedGroup === c ? 'bg-indigo-100 text-indigo-800' : 'hover:bg-gray-100'}`}>{c}</button>
          ))}
        </div>

        {/* Center: search + quantity filters + stock table */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center gap-2 p-2 border-b flex-wrap">
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Product name" className="flex-1 min-w-[160px] max-w-xs border rounded px-3 py-1.5 text-sm" />
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={qtyFilter === 'negative'} onChange={(e) => setQtyFilter(e.target.checked ? 'negative' : '')} /> Negative</label>
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={qtyFilter === 'nonzero'} onChange={(e) => setQtyFilter(e.target.checked ? 'nonzero' : '')} /> Non zero</label>
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={qtyFilter === 'zero'} onChange={(e) => setQtyFilter(e.target.checked ? 'zero' : '')} /> Zero</label>
            {stores.length > 0 && onStoreChange && (
              <select value={selectedStoreId} onChange={(e) => onStoreChange(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
                <option value="">All stores</option>
                {stores.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <span className="inline-flex gap-1">
                {summary.negative > 0 && <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800" title="Negative quantity">{summary.negative}</span>}
                <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800" title="Non-zero">{summary.positive}</span>
                {summary.zero > 0 && <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800" title="Zero">{summary.zero}</span>}
              </span>
              <span className="text-sm text-gray-500">Products count: {filteredProducts.length}</span>
            </div>
          </div>
          {error && <p className="text-red-600 text-sm px-4 py-1">{error}</p>}
          {loading ? (
            <p className="p-4 text-gray-500">Loading...</p>
          ) : (
            <>
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Name</th>
                      <th className="text-right p-2">Quantity</th>
                      <th className="text-left p-2">Unit</th>
                      <th className="text-right p-2">Cost price</th>
                      <th className="text-right p-2">Cost</th>
                      <th className="text-right p-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((p) => {
                      const q = p.quantityOnHand ?? 0;
                      const cost = p.cost ?? 0;
                      const value = p.value ?? q * cost;
                      const saleValue = q * (p.price ?? cost);
                      return (
                        <tr key={p._id} className={`border-b hover:bg-gray-50 ${q < 0 ? 'text-red-600' : ''}`}>
                          <td className="p-2">{p.sku || p._id?.slice(-6)}</td>
                          <td className="p-2 truncate max-w-[220px]" title={p.name}>{p.name}</td>
                          <td className="p-2 text-right">{q} pc</td>
                          <td className="p-2">pc</td>
                          <td className="p-2 text-right">{formatNumber(cost)}</td>
                          <td className="p-2 text-right">{formatNumber(value)}</td>
                          <td className="p-2 text-right">{formatNumber(saleValue)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredProducts.length === 0 && <p className="p-4 text-gray-500">No products found.</p>}
              </div>
              <div className="border-t px-4 py-2 bg-gray-50 flex justify-between text-sm">
                <span><strong>Cust price</strong> Total cost: {formatNumber(inventory?.totalValue ?? totalCost)}</span>
                <span><strong>Sale price</strong> Total: {formatNumber(totalSaleValue)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
